/**
 * V3 multi-market selector context.
 *
 * Holds the operator's "currently focused" market id so every page can
 * read it via `useMarket()` and every query can scope to it via
 * `useMarketQuery(...)`. The selection is persisted in localStorage so
 * the choice survives reloads.
 *
 * The default market is `btc-5m`. If the data-api catalogue has changed
 * and the persisted id is no longer present, we silently fall back to
 * the first available market.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { Market } from "../api/schemas";
import { useAuth } from "./useAuth";

const STORAGE_KEY = "gmvp.v3.selected_market_id";
const DEFAULT_MARKET_ID = "btc-5m";
const URL_PARAM = "market";

/**
 * Parse `?market=...` from a URL. Supports both regular query strings
 * (e.g. `/live?market=btc-15m`) and hash-router search portions
 * (e.g. `#/live?market=btc-15m`). Returns null when absent or invalid.
 */
function parseMarketFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const candidates: string[] = [];
  if (window.location.search) candidates.push(window.location.search);
  if (window.location.hash) {
    const qIdx = window.location.hash.indexOf("?");
    if (qIdx >= 0) candidates.push(window.location.hash.slice(qIdx));
  }
  for (const qs of candidates) {
    const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
    const v = params.get(URL_PARAM);
    if (v && /^[a-z][a-z0-9-]*$/.test(v)) return v;
  }
  return null;
}

/**
 * Mutate the hash-router URL to ``?market=<id>`` (or remove the param)
 * without triggering a navigation. We rewrite the hash because the
 * router uses hash history; `window.location.search` is unused.
 */
function writeMarketToUrl(id: string | null): void {
  if (typeof window === "undefined") return;
  const hash = window.location.hash || "#/";
  const [pathPart, queryPart] = (() => {
    const qIdx = hash.indexOf("?");
    return qIdx >= 0 ? [hash.slice(0, qIdx), hash.slice(qIdx + 1)] : [hash, ""];
  })();
  const params = new URLSearchParams(queryPart);
  if (id) params.set(URL_PARAM, id);
  else params.delete(URL_PARAM);
  const nextQuery = params.toString();
  const nextHash = nextQuery ? `${pathPart}?${nextQuery}` : pathPart;
  if (nextHash !== hash) {
    // `replaceState` avoids polluting browser history with one entry per
    // market switch; the operator can still copy/share the URL.
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}${nextHash}`);
  }
}

interface MarketContextValue {
  /** The selected market id (always non-empty). */
  marketId: string;
  /** The selected market record from the catalogue, or null while loading. */
  market: Market | null;
  /** All markets returned by the catalogue. Empty array while loading. */
  markets: Market[];
  /** Markets the operator has flagged enabled. Empty array while loading. */
  enabledMarkets: Market[];
  /** Change the active selection. */
  setMarketId: (id: string) => void;
  /** Status of the underlying catalogue query. */
  isLoading: boolean;
  isError: boolean;
}

const Ctx = createContext<MarketContextValue | null>(null);

function readPersisted(): string {
  if (typeof window === "undefined") return DEFAULT_MARKET_ID;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && /^[a-z][a-z0-9-]*$/.test(v) ? v : DEFAULT_MARKET_ID;
  } catch {
    return DEFAULT_MARKET_ID;
  }
}

function writePersisted(id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // localStorage is best-effort; ignore quota/private-mode errors.
  }
}

export function MarketProvider({ children }: { children: React.ReactNode }) {
  // Initial state: URL `?market=` wins (deep-link), then localStorage,
  // then the default. The catalogue effect later validates that the id
  // is still recognised.
  const [marketId, setMarketIdState] = useState<string>(
    () => parseMarketFromUrl() ?? readPersisted(),
  );
  // The provider wraps the login screen too: until the user has fully
  // signed in, never stamp `?market=` into the URL or fetch the catalogue —
  // a signed-out visitor stays on the bare landing URL.
  const { isReady: signedIn } = useAuth();

  const catalogue = useQuery({
    queryKey: ["v3", "markets"],
    queryFn: () => api.markets.list(),
    staleTime: 5 * 60_000, // catalogue rarely changes
    enabled: signedIn,
  });

  // Listen for external URL changes (back/forward, edited link).
  //   - If the new URL carries `?market=<id>` and differs from state,
  //     follow the URL.
  //   - If the new URL omits the param (typical when TanStack pushes a
  //     fresh route hash), re-stamp the current selection so the URL
  //     stays a valid deep link.
  useEffect(() => {
    if (typeof window === "undefined" || !signedIn) return;
    const onHashChange = () => {
      const fromUrl = parseMarketFromUrl();
      if (fromUrl) {
        if (fromUrl !== marketId) {
          setMarketIdState(fromUrl);
          writePersisted(fromUrl);
        }
      } else {
        writeMarketToUrl(marketId);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("popstate", onHashChange);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("popstate", onHashChange);
    };
  }, [marketId, signedIn]);

  const markets = useMemo(() => {
    const items = catalogue.data?.items ?? [];
    // Sort by window duration ascending, then asset alphabetically
    // (e.g. BTC 5m, ETH 5m, BTC 15m, ETH 15m, BTC 1h, ETH 1h, ...).
    return [...items].sort((a, b) => {
      if (a.window_seconds !== b.window_seconds) {
        return a.window_seconds - b.window_seconds;
      }
      return a.asset.localeCompare(b.asset);
    });
  }, [catalogue.data]);
  const enabledMarkets = useMemo(() => markets.filter((m) => m.enabled), [markets]);

  // If the catalogue loaded but the current id is no longer present,
  // gracefully fall back to the first available market.
  useEffect(() => {
    if (markets.length === 0) return;
    const exists = markets.some((m) => m.market_id === marketId);
    if (!exists) {
      const fallback = markets.find((m) => m.market_id === DEFAULT_MARKET_ID)
        ?? markets[0];
      if (fallback) {
        setMarketIdState(fallback.market_id);
        writePersisted(fallback.market_id);
      }
    }
  }, [markets, marketId]);

  const setMarketId = useCallback((id: string) => {
    setMarketIdState(id);
    writePersisted(id);
    writeMarketToUrl(id);
  }, []);

  // Keep the URL aligned with the active selection whenever the user
  // navigates between routes (TanStack pushes a new hash; we re-stamp
  // the `?market=` so deep linkability survives the navigation).
  useEffect(() => {
    if (signedIn) writeMarketToUrl(marketId);
  }, [marketId, signedIn]);

  const market = useMemo<Market | null>(
    () => markets.find((m) => m.market_id === marketId) ?? null,
    [markets, marketId],
  );

  const value = useMemo<MarketContextValue>(
    () => ({
      marketId,
      market,
      markets,
      enabledMarkets,
      setMarketId,
      isLoading: catalogue.isLoading,
      isError: catalogue.isError,
    }),
    [marketId, market, markets, enabledMarkets, setMarketId, catalogue.isLoading, catalogue.isError],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMarket(): MarketContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useMarket must be used inside <MarketProvider>");
  return v;
}

/**
 * Convenience hook for code paths that just need the current id (the
 * most common case). Equivalent to `useMarket().marketId`, but falls
 * back to `btc-5m` when no `<MarketProvider>` is mounted so callers
 * that may render outside the V3 shell (tests, isolated stories) keep
 * working.
 */
export function useMarketId(): string {
  const v = useContext(Ctx);
  return v?.marketId ?? DEFAULT_MARKET_ID;
}
